import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { getBrandingForEmployee } from '../config/brandingStore';
import { formatDate } from '../utils/dateUtils';

Font.register({
  family: 'Amiri',
  src: require('../assets/Amiri.ttf'),
});

const LeaveRequestPDF = ({ data }) => {
  const employee = data?.employee || {};
  const leave = data?.leave || {};

  const nom = employee.last_name || '…';
  const prenom = employee.first_name || '…';
  // Fill from employee poste and profession with fallbacks when associations aren't loaded
  const fonction = employee?.poste || '…';
  const structure = employee?.profession?.name
    || employee?.profession_name
    || data?.profession_name
    || '…';

  // Extract reference from leave or data
  const reference = leave?.reference_conge || data?.reference_conge || '……';

  const startDateObj = leave?.start_date ? new Date(leave.start_date) : null;
  const endDateObj = leave?.end_date ? new Date(leave.end_date) : null;
  const start = startDateObj ? formatDate(startDateObj) : '……';
  const end = endDateObj ? formatDate(endDateObj) : '……';
  // Calculate days difference (inclusive counting)
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    
    // Calculate difference in days (inclusive)
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return diffDays;
  };
  
  const computedDays = calculateDays(leave?.start_date, leave?.end_date);
  const apiDays = leave?.days != null ? parseFloat(leave.days) : null;
  const days = computedDays ?? apiDays ?? '……';
  const reason = leave?.reason || '';
  const phone = leave?.phone_number || employee?.phone || data?.phone || '……';
  const address = leave?.address_during_leave || data?.leave_address || '……';

  // Interim (nullable)
  const interim = leave?.interim || data?.interim || null;
  const interimName = interim ? `${interim.last_name || ''} ${interim.first_name || ''}`.trim() || '…' : '…';
  const interimFunction = interim?.poste || interim?.profession_name || '';

  // Type mapping. Expect optional leave.type in ['annuel','maternite','recuperation','sans_solde','exceptionnel','autre']
  const type = (leave?.type || '').toLowerCase();
  const is = (t) => (type === t ? '☒' : '☐');
  const typeLabelMap = {
    annuel: 'Annuel',
    maladie: 'Maladie',
    maternite: 'Maternité',
    recuperation: 'Récupération',
    sans_solde: 'Sans Solde',
    exceptionnel: 'Congé exceptionnel',
    autre: 'Autres',
  };
  const typeLabel = typeLabelMap[type] || '—';

  // Entitlement mapping: if annual, use employee balance; otherwise allow admin-provided value
  const entitlementDays = type === 'annuel'
    ? (employee?.leave_balance ?? '……')
    : (data?.entitlement_days ?? '……');

  // Manager (Responsable Hiérarchique) & Director (Directeur de la Structure)
  const manager = employee?.manager || data?.manager || null;
  const managerName = manager ? `${manager.last_name || ''} ${manager.first_name || ''}`.trim() || '…' : '…';
  const director = employee?.site?.director || data?.director || null;
  const directorName = director ? `${director.last_name || ''} ${director.first_name || ''}`.trim() || '…' : '…';
  // Approval dates
  const obsRespDate = formatDate(leave?.observation_responsable_at) || ' ';
  const obsDirDate = formatDate(leave?.observation_directeur_at) || ' ';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Title */}
          <Text style={styles.mainTitle}>DEMANDE DE CONGÉ</Text>
          {/* Header (keep) 
          <View style={styles.logo}>
            <Image src={getBranding().pdfLogo} style={styles.logoImage} />
          </View>*/}

          {/* I - Information employé */}
          <View style={styles.box}>
            <View style={[styles.boxHeader]}>
              <Text style={[styles.boxHeaderText]}>I- INFORMATION SUR L'EMPLOYE</Text>
            </View>
            <View style={styles.row}>
              <View style={[styles.cell, styles.cellHalf]}>
                <Text><Text style={styles.label}>Nom :</Text> {nom}</Text>
              </View>
              <View style={[styles.cell, styles.cellHalf]}>
                <Text><Text style={styles.label}>Prénom :</Text> {prenom}</Text>
              </View>
            </View>
            <View style={styles.row}>
              <View style={[styles.cell, styles.cellHalf]}>
                <Text><Text style={styles.label}>Service :</Text> {structure}</Text>
              </View>
              <View style={[styles.cell, styles.cellHalf]}>
                <Text><Text style={styles.label}>Poste :</Text> {fonction}</Text>
              </View>
            </View>
            <View style={styles.row}>
              <View style={[styles.cell, styles.cellFull]}>
                <Text><Text style={styles.label}>Référence :</Text> {reference}</Text>
              </View>
            </View>
          </View>

          {/* II - Type de congé */}
          <View style={styles.box}>
            <View style={styles.boxHeader}>
              <Text style={styles.boxHeaderText}>II- TYPE DE CONGÉ</Text>
            </View>
            <View style={styles.typeTable}>
              <View style={styles.typeTableRow}>
                <View style={styles.typeCell}>
                  <View style={styles.checkboxRow}>
                    <View style={styles.checkboxBox}>{type === 'annuel' ? <Text style={styles.checkboxMark}>X</Text> : null}</View>
                    <Text>Annuel</Text>
                  </View>
                </View>
                <View style={styles.typeCell}>
                  <View style={styles.checkboxRow}>
                    <View style={styles.checkboxBox}>{type === 'maladie' ? <Text style={styles.checkboxMark}>X</Text> : null}</View>
                    <Text>Maladie</Text>
                  </View>
                </View>
                <View style={[styles.typeCell, styles.typeCellLast]}>
                  <View style={styles.checkboxRow}>
                    <View style={styles.checkboxBox}>{type === 'maternite' ? <Text style={styles.checkboxMark}>X</Text> : null}</View>
                    <Text>Maternité</Text>
                  </View>
                </View>
              </View>
              <View style={styles.typeTableRow}>
                <View style={styles.typeCell}>
                  <View style={styles.checkboxRow}>
                    <View style={styles.checkboxBox}>{type === 'recuperation' ? <Text style={styles.checkboxMark}>X</Text> : null}</View>
                    <Text>Récupération</Text>
                  </View>
                </View>
                <View style={styles.typeCell}>
                  <View style={styles.checkboxRow}>
                    <View style={styles.checkboxBox}>{type === 'sans_solde' ? <Text style={styles.checkboxMark}>X</Text> : null}</View>
                    <Text>Sans Solde</Text>
                  </View>
                </View>
                <View style={[styles.typeCell, styles.typeCellLast]}>
                  <View style={styles.checkboxRow}>
                    <View style={styles.checkboxBox}>{type === 'exceptionnel' ? <Text style={styles.checkboxMark}>X</Text> : null}</View>
                    <Text>Congé exceptionnel</Text>
                  </View>
                </View>
              </View>
            </View>
            <View style={styles.typeRow}>
              <Text style={[styles.typeItem, { width: '98%' }]}><Text style={styles.label}>Motif :</Text> {reason || '……'}</Text>
            </View>
          </View>

          {/* Metrics + HR validation */}
          <View style={styles.splitRow}>
            <View style={[styles.leftPane]}>
              <Text style={styles.bullet}>- <Text style={styles.label}>Droit au congé :</Text> {entitlementDays} jours</Text>
              <Text style={styles.bullet}>- <Text style={styles.label}>Nombre de jours demandés :</Text> {days || ''} jours</Text>
              <Text style={styles.line}><Text style={styles.label}>Date de Départ :</Text> {start}</Text>
              <Text style={styles.line}><Text style={styles.label}>Date de Retour :</Text> {end}</Text>
              <Text style={styles.line}><Text style={styles.label}>Adresse durant le congé :</Text> {address}</Text>
              <Text style={[styles.line, { marginTop: 10 }]}><Text style={styles.label}>N° de Téléphone :</Text> {phone}</Text>
              <Text style={[styles.line, { marginTop: 4 }]}><Text style={styles.label}>Signature du Demandeur :</Text> </Text>
            </View>
            <View style={styles.rightPane}>
              <Text style={[styles.label, { marginBottom: 6 }]}>Validation du Droit de congé par la</Text>
              <Text style={[styles.label, { marginBottom: 6 }]}>Ressource Humaine :</Text>
              <View style={styles.hrBox} />
            </View>
          </View>

          {/* Interim person label + table aligned like template */}
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginTop: 16 }}>
            <View style={{ flex: 1, paddingTop: 6 }}>
              <Text style={styles.label}>Personne assurant{"\n"}l'Intérim (Cadres) :</Text>
            </View>
            <View style={[styles.table, { flex: 2, marginLeft: 16 }]}>
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.th, styles.col2]}>Nom et Prénom</Text>
                <Text style={[styles.th, styles.col2]}>Fonction</Text>
                <Text style={[styles.th, styles.col2]}>Signature</Text>
              </View>
              <View style={styles.tableBodyRow}>
                <Text style={[styles.td, styles.col2]}>{interimName}</Text>
                <Text style={[styles.td, styles.col2]}>{interimFunction || ' '}</Text>
                <Text style={[styles.td, styles.col2]}></Text>
              </View>
            </View>
          </View>

          {/* Approval table */}
          <View style={[styles.table, { marginTop: 16 }]}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.th, styles.col1]}>Avis et Accord</Text>
              <Text style={[styles.th, styles.col1]}>Nom et Prénom</Text>
              <Text style={[styles.th, styles.col1]}>Signature</Text>
              <Text style={[styles.th, styles.col1]}>Date</Text>
              <Text style={[styles.th, styles.col1]}>Observation</Text>
            </View>
            <View style={styles.tableBodyRow}>
              <Text style={[styles.td, styles.col1]}>Responsable{"\n"}Hiérarchique</Text>
              <Text style={[styles.td, styles.col1]}>{managerName}</Text>
              <Text style={[styles.td, styles.col1]}>{'   '}</Text>
              <Text style={[styles.td, styles.col1]}>{obsRespDate}</Text>
              <Text style={[styles.td, styles.col1]}>{leave?.observation_responsable || ''}</Text>
            </View>
            <View style={styles.tableBodyRow}>
              <Text style={[styles.td, styles.col1]}>Directeur{"\n"}de la Structure</Text>
              <Text style={[styles.td, styles.col1]}>{directorName}</Text>
              <Text style={[styles.td, styles.col1]}>{'   '}</Text>
              <Text style={[styles.td, styles.col1]}>{obsDirDate}</Text>
              <Text style={[styles.td, styles.col1]}>{leave?.observation_directeur || ''}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 11,
    lineHeight: 1.5,
    display: 'flex',
    flexDirection: 'column',
  },
  mainTitle: { fontSize: 14, fontWeight: 'bold', textAlign: 'center', textDecoration: 'underline', marginBottom: 8 },
  container: { flexGrow: 1, display: 'flex', flexDirection: 'column' },
  logo: { width: '100%', marginBottom: 10 },
  logoImage: { width: '100%' },
  // Generic boxes
  box: { borderWidth: 1, borderStyle: 'solid', borderColor: '#000', marginBottom: 10 },
  boxHeader: { borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#000', padding: 6, backgroundColor: '#fafafa' },
  boxHeaderText: { fontSize: 11, fontWeight: 'bold', textAlign: 'center', textDecoration: 'underline' },
  row: { flexDirection: 'row' },
  cell: { borderTopWidth: 1, borderStyle: 'solid', borderColor: '#000', padding: 6, minHeight: 24 },
  cellHalf: { width: '50%' },
  cellFull: { width: '100%' },
  label: { fontWeight: 'bold' },

  // Type section
  typeRow: { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 8, paddingVertical: 6 },
  typeItem: { width: '32%' },
  typeTable: { width: '100%', borderWidth: 1, borderStyle: 'solid', borderColor: '#000', marginTop: 4 },
  typeTableRow: { flexDirection: 'row', borderTopWidth: 1, borderStyle: 'solid', borderColor: '#000' },
  typeCell: { width: '33.33%', padding: 6, borderRightWidth: 1, borderStyle: 'solid', borderColor: '#000' },
  typeCellLast: { borderRightWidth: 0 },
  checkboxRow: { flexDirection: 'row', alignItems: 'center' },
  checkboxBox: { width: 14, height: 14, borderWidth: 1.5, borderStyle: 'solid', borderColor: '#000', marginRight: 6, alignItems: 'center', justifyContent: 'center' },
  checkboxMark: { fontSize: 10, fontWeight: 'bold', textAlign: 'center', lineHeight: 1 },

  // Split row under type
  splitRow: { flexDirection: 'row', marginTop: 6 },
  leftPane: { flex: 2, padding: 6, borderWidth: 1, borderStyle: 'solid', borderColor: '#000', minHeight: 150, marginRight: 10 },
  rightPane: { flex: 1, padding: 6, borderWidth: 1, borderStyle: 'solid', borderColor: '#000', minHeight: 150 },
  bullet: { marginBottom: 4 },
  line: { marginBottom: 4 },
  hrBox: { borderWidth: 1, borderStyle: 'solid', borderColor: '#000', height: 110, marginTop: 6 },

  // Table (interim and approvals)
  table: { width: '100%', borderWidth: 1, borderStyle: 'solid', borderColor: '#000' },
  tableHeaderRow: { flexDirection: 'row', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#000', backgroundColor: '#fafafa' },
  tableBodyRow: { flexDirection: 'row', borderTopWidth: 1, borderStyle: 'solid', borderColor: '#000', minHeight: 26 },
  th: { padding: 6, fontWeight: 'bold', borderRightWidth: 1, borderStyle: 'solid', borderColor: '#000', textAlign: 'center' },
  td: { padding: 6, borderRightWidth: 1, borderStyle: 'solid', borderColor: '#000' },
  col2: { width: '33.33%' },
  col1: { width: '25%' },
  captionRow: { borderTopWidth: 1, borderStyle: 'solid', borderColor: '#000' },
  caption: { padding: 6 },
});

export default LeaveRequestPDF;
